import { Info, PiggyBank, Tag } from '@phosphor-icons/react';

import { getGroupPlannedRemaining } from '../../utils/groupBudgetHeadroom';
import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';
import { MoneyDisplay } from '../common/MoneyDisplay';
import { HeroProgressBar } from '../common/HeroProgressBar';

/**
 * Camada 3 — categoria em foco: valor em display + barra de preenchimento
 * simples (sem marcas nem comparação com o método) + média como texto
 * simples quando há histórico.
 */
export function CategoryBudgetFocusCard({
  category,
  group,
  className,
}) {
  const isPot = Boolean(category.is_smoothing_pot);
  const budget =
    category.budget_eur !== null && category.budget_eur !== undefined
      ? Number(category.budget_eur)
      : 0;

  const groupRemaining = getGroupPlannedRemaining(group, {
    excludePotMonthly: isPot ? budget : 0,
  });
  const barMax = Math.max(
    groupRemaining != null ? budget + groupRemaining : budget,
    budget,
    1,
  );
  const groupDefined = Number(group?.categories_budget_sum_eur ?? group?.budget_eur ?? 0);

  const fillPct = Math.min(100, (budget / barMax) * 100);
  const average =
    category.average_spent_eur != null ? Number(category.average_spent_eur) : null;
  const showFixedNote =
    category.frequency === 'regular' &&
    category.budget_eur != null &&
    Number(category.budget_eur) > 0;

  const groupColor = group.color || category.category_color || '#5F7E54';

  return (
    <div
      className={cn('flex flex-col gap-space-3 rounded-ds-lg border-2 p-space-4', className)}
      style={{ borderColor: groupColor }}
    >
      <div className="flex items-center gap-space-2-5">
        <span
          className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-ds-md"
          style={{ backgroundColor: `${groupColor}22` }}
        >
          {isPot ? (
            <PiggyBank className="h-[18px] w-[18px]" style={{ color: groupColor }} aria-hidden />
          ) : (
            <Tag className="h-[18px] w-[18px]" style={{ color: groupColor }} aria-hidden />
          )}
        </span>
        <span className="type-title-card min-w-0 flex-1 truncate font-bold text-foreground">
          {category.category_name}
        </span>
      </div>

      <div className="flex items-center justify-center gap-space-3">
        <MoneyDisplay value={budget} className="type-display-hero tracking-[-0.03em] text-foreground" />
      </div>

      <HeroProgressBar
        variant="surface"
        percentage={fillPct}
        fillColor={groupColor}
        trackClassName="bg-muted"
        height={14}
        radius="rounded-ds-sm"
        ariaLabel={category.category_name}
      />

      <div className="flex items-center justify-between gap-space-2">
        <span className="type-chip font-normal text-muted-foreground">
          {pt.budget.category.ofGroup.replace('{amount}', formatEUR(groupDefined))}
        </span>
        {average != null ? (
          <span className="type-chip font-normal text-muted-foreground">
            {pt.budget.groupRefs.averageAmount.replace('{amount}', formatEUR(average))}
          </span>
        ) : null}
      </div>

      {showFixedNote ? (
        <p className="type-chip flex items-center gap-space-2 font-normal text-muted-foreground">
          <Info className="h-3 w-3 shrink-0" aria-hidden />
          {pt.budget.category.fixedBudgetNote}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Linha compacta de categoria (irmãs no grupo) — `.pen` → `ifCTC` › `irmãs`:
 * ícone + nome + média (inline, só quando há histórico) + valor.
 */
export function CategoryBudgetCompactRow({
  category,
  onSelect,
  className,
}) {
  const isPot = Boolean(category.is_smoothing_pot);
  const budget =
    category.budget_eur !== null && category.budget_eur !== undefined
      ? Number(category.budget_eur)
      : 0;
  const average =
    category.average_spent_eur != null ? Number(category.average_spent_eur) : null;
  const color = category.category_color || '#5F7E54';

  return (
    <div
      className={cn(
        'flex w-full items-center gap-space-2-5 border-b border-border py-space-2-5 last:border-b-0',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onSelect?.(category)}
        className="flex min-w-0 flex-1 items-center gap-space-2-5 text-left"
      >
        <span
          className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-ds-sm"
          style={{ backgroundColor: `${color}22` }}
        >
          {isPot ? (
            <PiggyBank className="h-4 w-4" style={{ color }} aria-hidden />
          ) : (
            <Tag className="h-4 w-4" style={{ color }} aria-hidden />
          )}
        </span>
        <span className="type-body-sm min-w-0 flex-1 truncate font-semibold text-foreground">
          {category.category_name}
        </span>
        {average != null ? (
          <span className="type-chip shrink-0 font-normal text-muted-foreground">
            {pt.budget.groupRefs.averageAmount.replace('{amount}', formatEUR(average))}
          </span>
        ) : null}
      </button>
      <MoneyDisplay value={budget} size="sm" className="type-display-row shrink-0" />
    </div>
  );
}
